import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
// mui
import { Box } from '@mui/material';
import { withStyles } from '@mui/styles';

// components
import { getCowbellFactorAggregations } from '../../console/customers/_services/dashboard.service';
import { MultiSelect } from '../../components/Selects';
import { GlobalMenuOption } from '../../components/Selects/MultiSelect/GlobalMenuOption';
import ToolTipIcon from '../../_assets/svg/Tooltip.svg';
import SimpleTooltip from '../../components/SimpleTooltip';
import { CowbellFactorsMapping } from '../../utility/CowbellFactorsMapping';
import { toUniversalDate } from '../../utils/date.utils';
import { getClosestTrendsIndex } from '../../console/customers/utils/graph.utils';
import { usePlatformLanguage } from '../../utils/hooks/usePlatformLanguage';
import { languages } from '../../i18n/i18n.language-config';

const CFHistory = ({ classes, q, accountId }: any) => {
  const [myData, setMyData] = useState(null);

  const region = usePlatformLanguage();
  const isUSRegion = region === languages['en-US'];

  const isCF2MarkersShown = isUSRegion;

  const crf2GoLiveDate = myData?.crf2GoLiveDate;

  const options = {
    chart: {
      type: 'line',
      height: '165px',
    },
    yAxis: {
      title: {
        text: '',
      },
      max: 80,
      lineColor: '#9192a4',
      lineWidth: 1,
      labels: {
        style: {
          color: '#c9cdd9',
        },
      },
    },
    title: {
      text: '',
    },
    xAxis: {
      type: 'datetime',
    },
    series: [],
    legend: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    responsive: true,
  };

  const cowbellFactors = [
    {
      label: 'Network Security',
      value: 'series3',
      color: '#6baedf',
      tooltip: CowbellFactorsMapping.networkSecurity.tooltip,
    },
    {
      label: 'Cloud Security',
      value: 'series4',
      color: '#4dc8e8',
      tooltip: CowbellFactorsMapping.cloudSecurity.tooltip,
    },
    {
      label: 'Endpoint Security',
      value: 'series5',
      color: '#83cebb',
      tooltip: CowbellFactorsMapping.endpointSecurity.tooltip,
    },
    {
      label: 'Dark Intelligence',
      value: 'series6',
      color: '#0098db',
      tooltip: CowbellFactorsMapping.darkIntelligence.tooltip,
    },
    {
      label: 'Funds Transfer',
      value: 'series7',
      color: '#7581e5',
      tooltip: CowbellFactorsMapping.fundsTransfer.tooltip,
    },
    {
      label: 'Cyber Extortion',
      value: 'series8',
      color: '#91afd9',
      tooltip: CowbellFactorsMapping.cyberExtortion.tooltip,
    },
    {
      label: 'Compliance',
      value: 'series9',
      color: '#00aea0',
      tooltip: CowbellFactorsMapping.compliance.tooltip,
    },
    {
      label: 'Supply Chain',
      value: 'series10',
      color: '#00bcb0',
      tooltip: CowbellFactorsMapping.supplyChain.tooltip,
    },
  ];

  const [selectedLines, setSelectedLines] = useState({
    series1: true,
    series2: true,
    series3: true,
    series4: true,
    series5: true,
    series6: true,
    series7: true,
    series8: true,
    series9: true,
    series10: true,
  });

  const [selectedOptions, setSelectedOptions] = useState(cowbellFactors);

  const chartRef = useRef(null);

  const toggleChartSeries = () => {
    const { chart } = chartRef.current;
    const keys = Object.keys(selectedLines);
    keys.forEach((item, idx) => {
      chart.series[idx].setVisible(selectedLines[item]);

      const chartLength = chart.series.length;
      const cf2SeriesOffset = idx + keys.length;

      // each CF has a corresponding series with go live data point
      if (isCF2MarkersShown && cf2SeriesOffset < chartLength) {
        chart.series[cf2SeriesOffset].setVisible(selectedLines[item]);
      }
    });
  };

  const onToggleLineHandler = (name: any) => () => {
    const val = !selectedLines[name];
    selectedLines[name] = val;

    toggleChartSeries();

    setSelectedLines((prevState) => {
      return {
        ...prevState,
        [name]: val,
      };
    });
  };

  const onToggleLineHandler1 = (name: any, option: any) => {
    const keys = Object.keys(selectedLines);
    keys.forEach((key) => {
      if (key !== 'series1' && key !== 'series2') {
        selectedLines[key] = false;
      }
    });

    option.forEach((line: any) => {
      const { value } = line;
      selectedLines[value] = true;
    });

    toggleChartSeries();

    setSelectedLines(selectedLines);
  };

  const toggleAllHandler = () => {
    const keys = Object.keys(selectedLines);
    if (selectedOptions.length > 0) {
      setSelectedOptions([]);
      keys.forEach((key) => {
        if (key !== 'series1' && key !== 'series2') {
          selectedLines[key] = false;
        }
      });
    } else {
      setSelectedOptions(cowbellFactors);
      keys.forEach((key) => {
        if (key !== 'series1' && key !== 'series2') {
          selectedLines[key] = true;
        }
      });
    }

    toggleChartSeries();

    setSelectedLines(selectedLines);
  };

  useEffect(() => {
    Promise.all([getCowbellFactorAggregations({ accountId, ...q })])
      .then((responses) => {
        setMyData(responses[0].data);
      })
      .catch(console.error.bind(console));
    // eslint-disable-next-line
  }, [q.before, q.after, q, accountId]);

  useEffect(() => {
    // Temporary solution for FF toggle to render shown CF2 series based on selected CF series
    const keys = Object.keys(selectedLines);
    const chart = chartRef?.current?.chart;

    if (chart) {
      keys.forEach((item, idx) => {
        chart.series[idx].setVisible(selectedLines[item]);

        const chartLength = chart.series.length;
        const cf2SeriesOffset = idx + keys.length;

        if (isCF2MarkersShown && cf2SeriesOffset < chartLength) {
          chart.series[cf2SeriesOffset].setVisible(selectedLines[item]);
        }
      });
    }
  }, [isCF2MarkersShown, selectedLines]);

  const renderHistoryChart = () => {
    const temp = {
      companyAggregate: {
        name: 'Organization Aggregate',
        data: [],
        color: CowbellFactorsMapping.companyAggregate.color,
      },
      industryAggregate: {
        name: 'Industry Aggregate',
        data: [],
        color: CowbellFactorsMapping.industryAggregate.color,
      },
      networkSecurity: {
        name: 'Network Security',
        data: [],
        color: '#6baedf',
      },
      cloudSecurity: {
        name: 'Cloud Security',
        data: [],
        color: '#4dc8e8',
      },
      endpointSecurity: {
        name: 'Endpoint Security',
        data: [],
        color: '#83cebb',
      },
      darkIntelligence: {
        name: 'Dark Intelligence',
        data: [],
        color: '#0098db',
      },

      fundsTransfer: {
        name: 'Funds Transfer',
        data: [],
        color: '#7581e5',
      },
      cyberExtortion: {
        name: 'Cyber Extortion',
        data: [],
        color: '#91afd9',
      },
      compliance: {
        name: 'Compliance',
        data: [],
        color: '#00aea0',
      },
      supplyChain: {
        name: 'Supply Chain',
        data: [],
        color: '#00bcb0',
      },
    };

    const keys = Object.keys(temp);

    const cf2Series: any = [];

    keys.forEach((key) => {
      const buckets = myData[key]?.buckets;

      // grabs the closest existing data point to attach CF 2.0 tooltip
      const closestIndex = getClosestTrendsIndex(buckets, crf2GoLiveDate);

      buckets.forEach((item: any, index: any) => {
        const dateValue = moment.utc(item.time).valueOf();

        temp[key].data.push([dateValue, item.value]);

        // create corresponding CF graph series with go live data point
        if (isCF2MarkersShown && index === closestIndex) {
          cf2Series.push(composeCF2Series(dateValue, item, crf2GoLiveDate));
        }
      });

      options.series.push(temp[key]);
    });

    if (isCF2MarkersShown) {
      options.series = options.series.concat(cf2Series);
    }

    return (
      <HighchartsReact
        ref={chartRef}
        highcharts={Highcharts}
        options={options}
      />
    );
  };

  if (myData) {
    const buttonPlaceHolder = (
      <p className={classes.legendText0}>Individual CFs</p>
    );

    return (
      <section className={classes.container}>
        <h3 className={classes.scoresHistoryTitle}>
          COWBELL FACTORS (CFs) - HISTORY
        </h3>
        <section className={classes.scoresHistoryContainer}>
          <section className={classes.chartLegends}>
            <Box
              className={classes.legendItem}
              onClick={onToggleLineHandler('series1')}
            >
              <div
                className={`${
                  selectedLines.series1
                    ? classes.legendIcon1
                    : classes.emptyLegendIcon1
                }`}
              />
              <p className={classes.legendText1}>Company Aggregate</p>
              <SimpleTooltip
                title={CowbellFactorsMapping.companyAggregate.tooltip}
                style={{ marginLeft: '0.2rem' }}
              >
                <ToolTipIcon />
              </SimpleTooltip>
            </Box>
            <Box
              className={classes.legendItem}
              onClick={onToggleLineHandler('series2')}
            >
              <div
                className={`${
                  selectedLines.series2
                    ? classes.legendIcon2
                    : classes.emptyLegendIcon2
                }`}
              />
              <p className={classes.legendText2}>Industry Aggregate</p>
              <SimpleTooltip
                title={CowbellFactorsMapping.industryAggregate.tooltip}
                style={{ marginLeft: '0.2rem' }}
              >
                <ToolTipIcon />
              </SimpleTooltip>
            </Box>
            <MultiSelect
              customLabel="Individual CFs"
              buttonPlaceholder={buttonPlaceHolder}
              options={cowbellFactors}
              onChange={onToggleLineHandler1}
              classes={{
                select: classes.button,
              }}
              supportSelectAll
              components={{
                Option: GlobalMenuOption,
              }}
              values={selectedOptions}
              toggleAllHandler={toggleAllHandler}
            />
          </section>
          <section className={classes.chartsContainer}>
            {renderHistoryChart()}
          </section>
        </section>
      </section>
    );
  }
  return null;
};

const styles = ({ palette }: any) => ({
  scoresHistoryTitle: {
    margin: '0 0 0.5rem 0.833rem !important',
    fontSize: '1rem',
    fontWeight: 'bold',
    lineHeight: 1.33,
    textAlign: 'left',
    color: palette.primary.grey,
    borderBottom: `1px solid ${palette.primary.modalBorder}`,
  },
  chartsContainer: {
    // `flex: 1` rather than `calc(100% - 15rem)`. The legend column is 14.5rem
    // and this one carried a 1rem left margin, so the two summed to 100% + 0.5
    // and the chart ran past the tile's right edge. A flex child takes what is
    // actually left, whatever the legend column is set to.
    flex: '1 1 auto',
    minWidth: 0,
    marginLeft: 'var(--tile-gutter)',
  },
  container: {
    width: '100%',
    // No pinned height. At 200px the chart drawn inside was taller than the
    // box containing it and spilled past the bottom edge.
    borderRadius: 'var(--tile-radius)',
    boxShadow: 'var(--tile-shadow)',
    padding: '1.083rem',
    backgroundColor: palette.background.lighter,
    marginBottom: 'var(--tile-gutter)',

    '& .highcharts-legend-item .highcharts-graph': {
      display: 'none',
    },
    '& .highcharts-background': {
      fill: palette.background.lighter,
    },
  },
  scoresHistoryContainer: {
    display: 'flex',
    flexDirection: 'row',
  },
  button: {
    padding: '5px !important',
    border: `1px solid ${palette.primary.oldBorder2}`,
  },
  chartLegends: {
    width: '14.5rem',
    height: '8.33rem',
    marginTop: 4,
  },
  buttonPlaceHolder: {
    display: 'flex',
    flexDirection: 'row',
    margin: '0 0 0 6px',
    cursor: 'pointer',
  },
  legendItem: {
    display: 'flex',
    flexDirection: 'row',
    margin: '0 0 0.9167rem 0.833rem',
    cursor: 'pointer',
  },
  legendIcon0: {
    width: '0.833rem',
    height: '0.833rem',
    backgroundColor: palette.primary.main,
    margin: '3px 10px 0 0',
  },
  emptyLegendIcon0: {
    width: '0.833rem',
    height: '0.833rem',
    border: `1px solid ${palette.primary.main}`,
    margin: '3px 10px 0 0',
  },
  legendText0: {
    fontSize: '0.833rem',
    lineHeight: 1.5,
    color: palette.primary.main,
    margin: '0 0 0 1.75rem !important',
  },

  legendIcon1: {
    width: '0.833rem',
    height: '0.833rem',
    backgroundColor: CowbellFactorsMapping.companyAggregate.color,
    margin: '3px 10px 0 0',
  },
  emptyLegendIcon1: {
    width: '0.833rem',
    height: '0.833rem',
    border: `1px solid ${CowbellFactorsMapping.companyAggregate.color}`,
    margin: '3px 10px 0 0',
  },
  legendText1: {
    fontSize: '1rem',
    fontWeight: 600,
    lineHeight: 1.33,
    color: CowbellFactorsMapping.companyAggregate.color,
    margin: '0px !important',
  },
  legendIcon2: {
    width: '0.833rem',
    height: '0.833rem',
    backgroundColor: CowbellFactorsMapping.industryAggregate.color,
    margin: '3px 10px 0 0',
  },
  emptyLegendIcon2: {
    width: '0.833rem',
    height: '0.833rem',
    border: `1px solid ${CowbellFactorsMapping.industryAggregate.color}`,
    margin: '3px 10px 0 0',
  },
  legendText2: {
    fontSize: '1rem',
    fontWeight: 600,
    lineHeight: 1.33,
    color: CowbellFactorsMapping.industryAggregate.color,
    margin: '0px !important',
  },
  buttonPlaceHolderContainer: {
    display: 'flex',
    flexDirection: 'row',
  },
});

function composeCF2Series(dateValue: any, item: any, crf2GoLiveDate: any) {
  return {
    data: [[dateValue, item.value]],
    color: '#fbc000',
    marker: {
      radius: 5,
      symbol: 'diamond',
    },
    tooltip: {
      headerFormat: `${toUniversalDate(
        moment.utc(new Date(crf2GoLiveDate)).format('MM-DD-YYYY')
      )}<br/>`,
      pointFormat:
        '<span style="color:{point.color}">●</span> Updated to CFs 2.0: {point.y}<br/>',
    },
  };
}

// @ts-expect-error TS(2345): Argument of type '({ palette }: any) => { scoresHi... Remove this comment to see the full error message
export default withStyles(styles, { withTheme: true })(CFHistory);
